import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { MoneyCheckDTO, MoneyCheckLineDTO, ReportFigures, ReportGranularity, ReportResponse, ReportTableDTO } from '@six-pockets/shared';
import { reportsApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { InlineAlert, PeriodPicker, SegmentedControl, type SegmentOption } from '@/components/ui';
import { RevenueTrendChart, type TrendSeries } from '@/features/reports/components/RevenueTrendChart';
import { ReportTable } from '@/features/reports/components/ReportTable';
import { currentKey, periodLabel, selectionPhrase, share } from '@/features/reports/utils';
import { cx } from '@/utils/cx';
import { formatINR, formatShortDuration } from '@/utils/format';
import styles from '@/styles/pages/ReportsPage/style.scss';

const GRANULARITY_OPTIONS: SegmentOption<ReportGranularity>[] = [
  { value: 'day', label: 'Daily' },
  { value: 'month', label: 'Monthly' },
];

const SERIES_OPTIONS: SegmentOption<TrendSeries>[] = [
  { value: 'total', label: 'Total' },
  { value: 'bookings', label: 'Bookings' },
  { value: 'condiments', label: 'Condiments' },
];

const messageOf = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

interface StatCardProps {
  label: string;
  value: number;
  caption: string;
  /** A second, quieter line. */
  note?: string;
  tone?: 'revenue' | 'due';
}

function StatCard({ label, value, caption, note, tone }: StatCardProps) {
  return (
    <div className={cx(styles.card, tone && styles[tone])}>
      <span className={styles.cardLabel}>{label}</span>
      <span className={styles.cardValue}>{formatINR(value)}</span>
      <span className={styles.cardCaption}>{caption}</span>
      {note && <span className={styles.cardNote}>{note}</span>}
    </div>
  );
}

/**
 * The six headline figures. Balance leads with what the period's bills owe now; for a single past day or month it
 * also says what they owed when it ended (the two differ once balances are collected later).
 */
function StatCards({ f, phrase, endedLabel }: { f: ReportFigures; phrase: string; endedLabel: string | null }) {
  const grossBookings = f.bookingsRevenue + f.discount;
  return (
    <section className={styles.cards} aria-label="Summary">
      <StatCard label="Revenue" value={f.revenue} tone="revenue" caption={`${plural(f.bills, 'bill')} ${phrase}`} />
      <StatCard label="Bookings revenue" value={f.bookingsRevenue} caption={`${share(f.bookingsRevenue, f.revenue)} of revenue, after discount`} />
      <StatCard label="Condiments revenue" value={f.condimentsRevenue} caption={`${share(f.condimentsRevenue, f.revenue)} of revenue`} />
      <StatCard
        label="Condiments profit"
        value={f.condimentsProfit}
        caption={f.condimentsRevenue > 0 ? `${share(f.condimentsProfit, f.condimentsRevenue)} margin on condiments` : 'No condiments sold'}
      />
      <StatCard label="Discount" value={f.discount} caption={f.discount > 0 ? `${share(f.discount, grossBookings)} off table time` : 'No discounts given'} />
      <StatCard
        label="Balance owed now"
        value={f.balanceNow}
        tone={f.balanceNow > 0 ? 'due' : undefined}
        caption={f.balanceNow > 0 ? `${plural(f.balanceNowBills, 'bill')} still owing` : 'Nothing owed'}
        note={endedLabel && f.balance !== f.balanceNow ? `${formatINR(f.balance)} at the end of ${endedLabel}` : undefined}
      />
    </section>
  );
}

/** How revenue splits between bookings and condiments, as one bar and its figures. */
function RevenueSplit({ f }: { f: ReportFigures }) {
  const b = f.revenue > 0 ? (f.bookingsRevenue / f.revenue) * 100 : 0;
  const c = f.revenue > 0 ? 100 - b : 0;
  return (
    <section className={styles.panel}>
      <div className={styles.panelHead}>
        <h2 className={styles.panelTitle}>Revenue split</h2>
        <span className={styles.panelSub}>Where the revenue came from</span>
      </div>

      <div className={styles.splitBar} role="img" aria-label={`Bookings ${share(f.bookingsRevenue, f.revenue)}, condiments ${share(f.condimentsRevenue, f.revenue)}`}>
        {f.revenue === 0 ? (
          <span className={styles.splitEmpty} />
        ) : (
          <>
            {b > 0 && <span className={styles.segBookings} style={{ flexBasis: `${b}%` }} />}
            {c > 0 && <span className={styles.segCondiments} style={{ flexBasis: `${c}%` }} />}
          </>
        )}
      </div>

      <ul className={styles.lines}>
        <li className={styles.line}>
          <span className={cx(styles.dot, styles.dotBookings)} />
          <span className={styles.lineLabel}>Bookings</span>
          <span className={styles.lineShare}>{share(f.bookingsRevenue, f.revenue)}</span>
          <span className={styles.lineValue}>{formatINR(f.bookingsRevenue)}</span>
        </li>
        <li className={styles.line}>
          <span className={cx(styles.dot, styles.dotCondiments)} />
          <span className={styles.lineLabel}>Condiments</span>
          <span className={styles.lineShare}>{share(f.condimentsRevenue, f.revenue)}</span>
          <span className={styles.lineValue}>{formatINR(f.condimentsRevenue)}</span>
        </li>
        <li className={cx(styles.line, styles.lineTotal)}>
          <span className={styles.lineLabel}>Revenue</span>
          <span className={styles.lineValue}>{formatINR(f.revenue)}</span>
        </li>
      </ul>

      {f.waived.bills > 0 && (
        <p className={styles.note}>
          Waived: {plural(f.waived.bills, 'bill')} · {formatINR(f.waived.amount)}, not in revenue
        </p>
      )}
    </section>
  );
}

/**
 * Money received: UPI to match with the bank, cash with the drawer. For a single day, UPI and Cash each open that
 * day's payments of that kind (Bills → Payments), and "Older balances collected" opens that day's recoveries on
 * Balance bills (which bills, how much each).
 */
function Collection({ f, day }: { f: ReportFigures; day: string | null }) {
  const c = f.collection;
  return (
    <section className={styles.panel}>
      <div className={styles.panelHead}>
        <h2 className={styles.panelTitle}>Collection</h2>
        <span className={styles.panelSub}>Match UPI with the bank and cash with the drawer</span>
      </div>

      <div className={styles.collectTotal}>
        <span className={styles.cardLabel}>Collected</span>
        <span className={styles.collectValue}>{formatINR(c.total)}</span>
        <span className={styles.cardCaption}>{plural(c.payments, 'payment')}</span>
      </div>

      <div className={styles.methods}>
        <div className={styles.method}>
          <span className={styles.cardLabel}>UPI</span>
          <span className={styles.methodValue}>{formatINR(c.upi)}</span>
          {day && c.upi > 0 && (
            <Link className={styles.methodLink} to={`/bills/payments?method=upi&date=${day}`}>
              Show payments
            </Link>
          )}
        </div>
        <div className={styles.method}>
          <span className={styles.cardLabel}>Cash</span>
          <span className={styles.methodValue}>{formatINR(c.cash)}</span>
          {day && c.cash > 0 && (
            <Link className={styles.methodLink} to={`/bills/payments?method=cash&date=${day}`}>
              Show payments
            </Link>
          )}
        </div>
      </div>

      <ul className={styles.lines}>
        <li className={styles.line}>
          <span className={styles.lineLabel}>On these bills</span>
          <span className={styles.lineValue}>{formatINR(c.fromPeriodBills)}</span>
        </li>
        <li className={styles.line}>
          <span className={styles.lineLabel}>
            Older balances collected
            {day && c.olderBalances > 0 && (
              <Link className={styles.lineLink} to={`/bills/balance?view=recovered&date=${day}`}>
                See bills
              </Link>
            )}
          </span>
          <span className={styles.lineValue}>{formatINR(c.olderBalances)}</span>
        </li>
      </ul>
    </section>
  );
}

/**
 * Table time by table, biggest first: each table's net (after its share of the bills' discounts) with a bar against
 * the top table, its bookings and time charged. The nets add up to the Bookings revenue card.
 */
function TableRevenue({ tables, total, phrase }: { tables: ReportTableDTO[]; total: number; phrase: string }) {
  const top = tables[0]?.net ?? 0;
  return (
    <section className={styles.panel}>
      <div className={styles.panelHead}>
        <h2 className={styles.panelTitle}>Revenue by table</h2>
        <span className={styles.panelSub}>Table time after discount, by bill date</span>
      </div>

      {tables.length === 0 ? (
        <p className={styles.note}>No table time billed {phrase}</p>
      ) : (
        <ul className={styles.lines}>
          {tables.map((t) => (
            <li key={t.table} className={styles.tableLine}>
              <div className={styles.tableTop}>
                <span className={styles.tableName}>{t.table}</span>
                <span className={styles.lineShare}>{share(t.net, total)}</span>
                <span className={styles.lineValue}>{formatINR(t.net)}</span>
              </div>
              <div className={styles.tableBar}>
                <span style={{ width: `${top > 0 ? Math.max(0, (t.net / top) * 100) : 0}%` }} />
              </div>
              <span className={styles.tableMeta}>
                {plural(t.bookings, 'booking')} · {formatShortDuration(t.timeMs)}
                {t.discount > 0 && ` · ${formatINR(t.gross)} less ${formatINR(t.discount)} discount`}
              </span>
            </li>
          ))}
          <li className={cx(styles.line, styles.lineTotal)}>
            <span className={styles.lineLabel}>Table time</span>
            <span className={styles.lineValue}>{formatINR(total)}</span>
          </li>
        </ul>
      )}

      {tables.some((t) => t.discount > 0) && <p className={styles.note}>A bill's discount is split across its bookings by amount.</p>}
    </section>
  );
}

/** One side of the money check: what staff reported, and how it compares with the day's collection of it. */
function CheckCard({ label, line, missing }: { label: string; line: MoneyCheckLineDTO | null; missing: string }) {
  const d = line?.difference ?? 0;
  return (
    <div className={styles.method}>
      <span className={styles.cardLabel}>{label}</span>
      <span className={styles.methodValue}>{line ? formatINR(line.actual) : '—'}</span>
      {line ? (
        <span className={cx(styles.check, d === 0 ? styles.checkOk : d < 0 ? styles.checkShort : styles.checkExtra)}>
          {d === 0 ? 'Matches' : `${formatINR(Math.abs(d))} ${d < 0 ? 'deficit' : 'surplus'}`}
        </span>
      ) : (
        <span className={styles.cardCaption}>{missing}</span>
      )}
    </div>
  );
}

/**
 * A day's money check, kept short: a UPI card and a Cash card, each with what staff reported in the Cash book and
 * whether it matches the day's collection of it ("Matches", "₹x deficit", "₹x surplus"), then the total of the two
 * (once both are in).
 */
function MoneyCheck({ check }: { check: MoneyCheckDTO }) {
  const { upi, cash } = check;
  const total =
    upi && cash ? { expected: upi.expected + cash.expected, actual: upi.actual + cash.actual, difference: upi.difference + cash.difference } : null;
  return (
    <section className={styles.panel}>
      <h2 className={styles.panelTitle}>Money check</h2>
      <div className={styles.checkCards}>
        <CheckCard label="UPI reported" line={upi} missing="Not logged" />
        <CheckCard label="Cash reported" line={cash} missing="Not counted" />
        <CheckCard label="Total reported" line={total} missing="Needs both" />
      </div>
    </section>
  );
}

/**
 * Reports (admin): revenue by bill date and collection by payment time, for a day or month (today / this month by
 * default) or a range of them. Cards, the revenue split and collection cover the selection; the trend shows each
 * day (month) of a range, or the 7 days (6 months) up to a single one. A range gets a breakdown table of its
 * periods; a single month gets one of its days (never for several months: too many rows). A single day gets a
 * short money check once its cash or UPI count is logged.
 */
export function ReportsPage() {
  const [granularity, setGranularity] = useState<ReportGranularity>('day');
  const [range, setRange] = useState(() => ({ from: currentKey('day'), to: currentKey('day') }));
  const [series, setSeries] = useState<TrendSeries>('total');

  const [report, setReport] = useState<ReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    reportsApi.get({ granularity, ...range }, ctrl.signal).then(
      (r) => {
        setReport(r);
        setError(null);
        setLoading(false);
      },
      (err) => {
        if (ctrl.signal.aborted) return;
        setError(messageOf(err));
        setLoading(false);
      },
    );
    return () => ctrl.abort();
  }, [granularity, range]);

  const changeGranularity = (g: ReportGranularity) => {
    setGranularity(g);
    setRange({ from: currentKey(g), to: currentKey(g) });
  };

  // The report on screen may still be the previous selection while the next one loads; label it by its own.
  const isRange = report ? report.from !== report.to : false;
  const unit = report?.granularity === 'month' ? 'month' : 'day';
  const trendSub = !report
    ? ''
    : isRange
      ? `Each ${unit}, ${periodLabel(report.granularity, report.from)} → ${periodLabel(report.granularity, report.to)}`
      : report.granularity === 'day'
        ? `The 7 days up to ${periodLabel('day', report.to)}`
        : `The 6 months up to ${periodLabel('month', report.to)}`;

  return (
    <>
      <PageHeader title="Reports" />

      <div className={styles.toolbar}>
        <div className={styles.granularity}>
          <SegmentedControl legend="Report by" hideLegend columns={2} options={GRANULARITY_OPTIONS} value={granularity} onChange={changeGranularity} />
        </div>
        <div className={styles.period}>
          <PeriodPicker
            label="Report period"
            granularity={granularity}
            from={range.from}
            to={range.to}
            onChange={(from, to) => setRange({ from, to })}
          />
        </div>
      </div>

      {error && <InlineAlert message={error} onDismiss={() => setError(null)} />}

      {!report ? (
        <p className={styles.empty}>{loading ? 'Loading report…' : 'No report'}</p>
      ) : (
        <div className={cx(styles.body, loading && styles.busy)} aria-busy={loading}>
          <StatCards
            f={report.summary}
            phrase={selectionPhrase(report.granularity, report.from, report.to)}
            endedLabel={isRange || report.to === currentKey(report.granularity) ? null : periodLabel(report.granularity, report.to)}
          />

          <div className={styles.pair}>
            <RevenueSplit f={report.summary} />
            <Collection f={report.summary} day={report.granularity === 'day' && !isRange ? report.from : null} />
          </div>

          {report.moneyCheck && <MoneyCheck check={report.moneyCheck} />}

          <TableRevenue
            tables={report.tables}
            total={report.summary.bookingsRevenue}
            phrase={selectionPhrase(report.granularity, report.from, report.to)}
          />

          <section className={styles.panel}>
            <div className={styles.panelHeadRow}>
              <div className={styles.panelHead}>
                <h2 className={styles.panelTitle}>Revenue trend</h2>
                <span className={styles.panelSub}>{trendSub}</span>
              </div>
              <div className={styles.series}>
                <SegmentedControl legend="Show" hideLegend columns={3} options={SERIES_OPTIONS} value={series} onChange={setSeries} />
              </div>
            </div>
            <RevenueTrendChart periods={report.periods} granularity={report.granularity} series={series} />
          </section>

          {isRange ? (
            <section className={styles.panel}>
              <div className={styles.panelHead}>
                <h2 className={styles.panelTitle}>{unit === 'day' ? 'Daily' : 'Monthly'} breakdown</h2>
                <span className={styles.panelSub}>Each {unit} in the range, newest first</span>
              </div>
              <ReportTable periods={report.periods} granularity={report.granularity} />
            </section>
          ) : (
            report.days && (
              <section className={styles.panel}>
                <div className={styles.panelHead}>
                  <h2 className={styles.panelTitle}>Daily breakdown</h2>
                  <span className={styles.panelSub}>Each day {selectionPhrase('month', report.from, report.to)}, newest first</span>
                </div>
                <ReportTable periods={report.days} granularity="day" />
              </section>
            )
          )}
        </div>
      )}
    </>
  );
}
